package com.qrkontaq.ui.tenant

/**
 * UI chrome strings of the guest menu site, covering every language a venue may
 * enable (see SITE_LANGUAGE_OPTIONS): en, uk, pl, ru, de, es, fr. Dish/category
 * content comes translated from the owner; this file is only the fixed UI text.
 */
internal class MenuStrings(
    val popular: String,
    val chefRecommends: String,
    val outOfStock: String,
    val kcalUnit: String,
    val allergensPrefix: String,
    val close: String,
    val share: String,
    val linkCopied: String,
    val filters: String,
    val reset: String,
    val done: String,
    val resetFilters: String,
    val nothingFound: String,
    val tryDifferentFilters: String,
    private val filterNames: Map<String, String>,
    private val tagNames: Map<String, String>,
    private val allergenNames: Map<String, String>,
    val ratePlace: String,
    val thanksRating: String,
    val alreadyRated: String,
    val submitFailed: String,
    val tapStar: String,
    val waiter: String,
    val waiterComing: String,
    private val tableWord: String,
    val callWaiter: String,
    val bringBill: String,
    val myCheck: String,
    val loading: String,
    val noOrdersYet: String,
    val checkHint: String,
    val scanTableTitle: String,
    val scanTableHint: String,
    val total: String,
    val updatesLive: String,
    val open: String,
    val closed: String,
    private val untilWord: String,
    private val opensWord: String,
    val aboutUs: String,
    val addressLabel: String,
    val phoneLabel: String,
    val workingHours: String,
    val closedToday: String,
    val schedule: String,
    val daysShort: List<String>, // mon..sun
    val closedDay: String,
    val followUs: String,
    val getDirections: String,
    val contacts: String,
    val visitWebsite: String,
    val information: String,
    val today: String,
    val allergenList: String,
    val allergenIntro: String,
    val aboutCompany: String,
    val legal: String,
    val cookiePolicy: String,
    val terms: String,
    val privacy: String,
    val madeWith: String,
    val venueNotFound: String,
    val menuUnavailable: String,
    val signInHint: String,
    val continueWithGoogle: String,
    val continueWithApple: String,
    val signOut: String,
    val loyaltySectionTitle: String,
    val loyaltySectionDescription: String,
    private val loyaltyCashbackTpl: String, // "{p}" -> percent
    val loyaltyBalanceLabel: String,
    val loyaltyRefreshCode: String,
    val loyaltyCodeHint: String,
    val loyaltyQrUnavailable: String,
    val loyaltyLoadFailed: String,
    val loyaltySignInFailed: String,
) {
    val loyaltySignInGoogle: String get() = continueWithGoogle
    val loyaltySignInApple: String get() = continueWithApple

    fun loyaltyCashbackRate(percent: Int) = loyaltyCashbackTpl.replace("{p}", "$percent")

    fun filterLabel(key: String) = filterNames[key] ?: key

    fun tag(code: String) = tagNames[code] ?: code

    fun allergen(code: String) = allergenNames[code] ?: code

    fun kcal(value: Int) = "$value $kcalUnit"

    fun table(label: String) = "$tableWord $label"

    fun openUntil(boundary: String) = "$open · $untilWord $boundary"

    fun openUntilPlain(boundary: String) = "$open $untilWord $boundary"

    fun closedOpens(boundary: String) = "$closed · $opensWord $boundary"
}

private val MENU_EN =
    MenuStrings(
        popular = "Popular",
        chefRecommends = "Chef recommends",
        outOfStock = "Out of stock",
        kcalUnit = "kcal",
        allergensPrefix = "Allergens: ",
        close = "Close",
        share = "Share",
        linkCopied = "Link copied",
        filters = "Filters",
        reset = "Reset",
        done = "Done",
        resetFilters = "Reset filters",
        nothingFound = "Nothing found",
        tryDifferentFilters = "Try different filters",
        filterNames = mapOf("vegan" to "Vegan", "spicy" to "Spicy", "gluten-free" to "Gluten-free"),
        tagNames = mapOf("vegan" to "vegan", "spicy" to "spicy", "new" to "new"),
        allergenNames =
            mapOf(
                "gluten" to "gluten", "lactose" to "lactose", "nuts" to "nuts",
                "eggs" to "eggs", "fish" to "fish", "soy" to "soy",
            ),
        ratePlace = "Rate the place",
        thanksRating = "Thanks for the rating!",
        alreadyRated = "You already rated this place",
        submitFailed = "Could not submit — try again",
        tapStar = "Tap a star",
        waiter = "Waiter",
        waiterComing = "Got it — coming over!",
        tableWord = "Table",
        callWaiter = "Call a waiter",
        bringBill = "Bring the bill",
        myCheck = "My check",
        loading = "Loading…",
        noOrdersYet = "No orders yet",
        checkHint = "Your check appears here as soon as the waiter starts your order.",
        scanTableTitle = "Scan your table's QR code",
        scanTableHint = "Call a waiter or ask for the bill after scanning the QR code on your table.",
        total = "Total",
        updatesLive = "Updates live as your order changes",
        open = "Open",
        closed = "Closed",
        untilWord = "until",
        opensWord = "opens",
        aboutUs = "About us",
        addressLabel = "Address",
        phoneLabel = "Phone",
        workingHours = "Working hours",
        closedToday = "closed today",
        schedule = "Schedule",
        daysShort = listOf("Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"),
        closedDay = "closed",
        followUs = "Follow us",
        getDirections = "Get directions",
        contacts = "Contacts",
        visitWebsite = "Visit our website",
        information = "Information",
        today = "Today",
        allergenList = "Allergen list",
        allergenIntro = "Dish cards mark these allergens: ",
        aboutCompany = "About the company",
        legal = "Legal",
        cookiePolicy = "Cookie policy",
        terms = "Terms of service",
        privacy = "Privacy policy",
        madeWith = "Made with ",
        venueNotFound = "Venue not found",
        menuUnavailable = "The menu is temporarily unavailable",
        signInHint =
            "Signing in makes ordering easier and remembers your actions — " +
                "see it all in the QRkontaq app.",
        continueWithGoogle = "Continue with Google",
        continueWithApple = "Continue with Apple",
        signOut = "Sign out",
        loyaltySectionTitle = "Loyalty program",
        loyaltySectionDescription = "Sign up to get your personal QR code and collect bonuses.",
        loyaltyCashbackTpl = "{p}% cashback on your bill",
        loyaltyBalanceLabel = "Your balance",
        loyaltyRefreshCode = "New code",
        loyaltyCodeHint = "Show the QR or tell the code to your waiter",
        loyaltyQrUnavailable = "QR is unavailable — just tell the code to your waiter",
        loyaltyLoadFailed = "Could not load — try again",
        loyaltySignInFailed = "Sign-in failed — try again",
    )

private val MENU_UK =
    MenuStrings(
        popular = "Популярне",
        chefRecommends = "Рекомендує шеф",
        outOfStock = "Немає в наявності",
        kcalUnit = "ккал",
        allergensPrefix = "Алергени: ",
        close = "Закрити",
        share = "Поділитись",
        linkCopied = "Скопійовано",
        filters = "Фільтри",
        reset = "Скинути",
        done = "Готово",
        resetFilters = "Скинути фільтри",
        nothingFound = "Нічого не знайдено",
        tryDifferentFilters = "Спробуйте інші фільтри",
        filterNames = mapOf("vegan" to "Веган", "spicy" to "Гостре", "gluten-free" to "Без глютену"),
        tagNames = mapOf("vegan" to "веган", "spicy" to "гостре", "new" to "новинка"),
        allergenNames =
            mapOf(
                "gluten" to "глютен", "lactose" to "лактоза", "nuts" to "горіхи",
                "eggs" to "яйця", "fish" to "риба", "soy" to "соя",
            ),
        ratePlace = "Оцініть заклад",
        thanksRating = "Дякуємо за оцінку!",
        alreadyRated = "Ви вже оцінили цей заклад",
        submitFailed = "Не вдалося — спробуйте ще раз",
        tapStar = "Торкніться зірки",
        waiter = "Офіціант",
        waiterComing = "Прийнято — вже йдемо!",
        tableWord = "Стіл",
        callWaiter = "Покликати офіціанта",
        bringBill = "Принести рахунок",
        myCheck = "Мій чек",
        loading = "Завантаження…",
        noOrdersYet = "Замовлень поки немає",
        checkHint = "Ваш чек з'явиться тут, щойно офіціант прийме замовлення.",
        scanTableTitle = "Відскануйте QR-код вашого столу",
        scanTableHint = "Покликати офіціанта чи попросити рахунок можна після сканування QR-коду на вашому столі.",
        total = "Разом",
        updatesLive = "Оновлюється автоматично при зміні замовлення",
        open = "Відчинено",
        closed = "Зачинено",
        untilWord = "до",
        opensWord = "з",
        aboutUs = "Про нас",
        addressLabel = "Адреса",
        phoneLabel = "Телефон",
        workingHours = "Години роботи",
        closedToday = "сьогодні вихідний",
        schedule = "Розклад",
        daysShort = listOf("Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"),
        closedDay = "вихідний",
        followUs = "Ми в соцмережах",
        getDirections = "Прокласти маршрут",
        contacts = "Контакти",
        visitWebsite = "Завітайте на наш веб-сайт",
        information = "Інформація",
        today = "Сьогодні",
        allergenList = "Список алергенів",
        allergenIntro = "У картках страв позначаються такі алергени: ",
        aboutCompany = "Інформація про компанію",
        legal = "Правова інформація",
        cookiePolicy = "Політика використання файлів cookie",
        terms = "Умови користування",
        privacy = "Політика конфіденційності",
        madeWith = "Створено на ",
        venueNotFound = "Заклад не знайдено",
        menuUnavailable = "Меню тимчасово недоступне",
        signInHint =
            "Авторизація спрощує процес і запам'ятовує ваші дії — " +
                "усе це можна побачити в застосунку QRkontaq.",
        continueWithGoogle = "Продовжити з Google",
        continueWithApple = "Продовжити з Apple",
        signOut = "Вийти з акаунта",
        loyaltySectionTitle = "Система лояльності",
        loyaltySectionDescription = "Зареєструйтеся, щоб отримати персональний QR-код і накопичувати бонуси.",
        loyaltyCashbackTpl = "Кешбек {p}% з чека",
        loyaltyBalanceLabel = "Ваш баланс",
        loyaltyRefreshCode = "Новий код",
        loyaltyCodeHint = "Покажіть QR або назвіть код офіціанту",
        loyaltyQrUnavailable = "QR недоступний — просто назвіть код офіціанту",
        loyaltyLoadFailed = "Не вдалося завантажити — спробуйте ще раз",
        loyaltySignInFailed = "Не вдалося увійти — спробуйте ще раз",
    )

private val MENU_PL =
    MenuStrings(
        popular = "Popularne",
        chefRecommends = "Szef kuchni poleca",
        outOfStock = "Niedostępne",
        kcalUnit = "kcal",
        allergensPrefix = "Alergeny: ",
        close = "Zamknij",
        share = "Udostępnij",
        linkCopied = "Skopiowano",
        filters = "Filtry",
        reset = "Resetuj",
        done = "Gotowe",
        resetFilters = "Wyczyść filtry",
        nothingFound = "Nic nie znaleziono",
        tryDifferentFilters = "Spróbuj innych filtrów",
        filterNames = mapOf("vegan" to "Wegańskie", "spicy" to "Ostre", "gluten-free" to "Bez glutenu"),
        tagNames = mapOf("vegan" to "wegańskie", "spicy" to "ostre", "new" to "nowość"),
        allergenNames =
            mapOf(
                "gluten" to "gluten", "lactose" to "laktoza", "nuts" to "orzechy",
                "eggs" to "jajka", "fish" to "ryby", "soy" to "soja",
            ),
        ratePlace = "Oceń lokal",
        thanksRating = "Dziękujemy za ocenę!",
        alreadyRated = "Ten lokal został już oceniony",
        submitFailed = "Nie udało się — spróbuj ponownie",
        tapStar = "Dotknij gwiazdki",
        waiter = "Kelner",
        waiterComing = "Przyjęte — już idziemy!",
        tableWord = "Stolik",
        callWaiter = "Zawołaj kelnera",
        bringBill = "Przynieś rachunek",
        myCheck = "Mój rachunek",
        loading = "Ładowanie…",
        noOrdersYet = "Brak zamówień",
        checkHint = "Twój rachunek pojawi się tutaj, gdy kelner przyjmie zamówienie.",
        scanTableTitle = "Zeskanuj kod QR swojego stolika",
        scanTableHint = "Zawołaj kelnera lub poproś o rachunek po zeskanowaniu kodu QR na Twoim stoliku.",
        total = "Razem",
        updatesLive = "Aktualizuje się automatycznie przy zmianie zamówienia",
        open = "Otwarte",
        closed = "Zamknięte",
        untilWord = "do",
        opensWord = "od",
        aboutUs = "O nas",
        addressLabel = "Adres",
        phoneLabel = "Telefon",
        workingHours = "Godziny otwarcia",
        closedToday = "dziś nieczynne",
        schedule = "Harmonogram",
        daysShort = listOf("Pn", "Wt", "Śr", "Cz", "Pt", "So", "Nd"),
        closedDay = "nieczynne",
        followUs = "Obserwuj nas",
        getDirections = "Wyznacz trasę",
        contacts = "Kontakt",
        visitWebsite = "Odwiedź naszą stronę",
        information = "Informacje",
        today = "Dziś",
        allergenList = "Lista alergenów",
        allergenIntro = "Karty dań oznaczają następujące alergeny: ",
        aboutCompany = "O firmie",
        legal = "Informacje prawne",
        cookiePolicy = "Polityka cookies",
        terms = "Regulamin",
        privacy = "Polityka prywatności",
        madeWith = "Stworzono w ",
        venueNotFound = "Nie znaleziono lokalu",
        menuUnavailable = "Menu jest tymczasowo niedostępne",
        signInHint =
            "Logowanie ułatwia proces i zapamiętuje Twoje działania — " +
                "wszystko zobaczysz w aplikacji QRkontaq.",
        continueWithGoogle = "Kontynuuj z Google",
        continueWithApple = "Kontynuuj z Apple",
        signOut = "Wyloguj się",
        loyaltySectionTitle = "Program lojalnościowy",
        loyaltySectionDescription = "Zarejestruj się, aby otrzymać osobisty kod QR i zbierać bonusy.",
        loyaltyCashbackTpl = "{p}% cashbacku od rachunku",
        loyaltyBalanceLabel = "Twoje saldo",
        loyaltyRefreshCode = "Nowy kod",
        loyaltyCodeHint = "Pokaż QR lub podaj kod kelnerowi",
        loyaltyQrUnavailable = "QR niedostępny — po prostu podaj kod kelnerowi",
        loyaltyLoadFailed = "Nie udało się załadować — spróbuj ponownie",
        loyaltySignInFailed = "Logowanie nie powiodło się — spróbuj ponownie",
    )

private val MENU_RU =
    MenuStrings(
        popular = "Популярное",
        chefRecommends = "Рекомендует шеф",
        outOfStock = "Нет в наличии",
        kcalUnit = "ккал",
        allergensPrefix = "Аллергены: ",
        close = "Закрыть",
        share = "Поделиться",
        linkCopied = "Скопировано",
        filters = "Фильтры",
        reset = "Сбросить",
        done = "Готово",
        resetFilters = "Сбросить фильтры",
        nothingFound = "Ничего не найдено",
        tryDifferentFilters = "Попробуйте другие фильтры",
        filterNames = mapOf("vegan" to "Веган", "spicy" to "Острое", "gluten-free" to "Без глютена"),
        tagNames = mapOf("vegan" to "веган", "spicy" to "острое", "new" to "новинка"),
        allergenNames =
            mapOf(
                "gluten" to "глютен", "lactose" to "лактоза", "nuts" to "орехи",
                "eggs" to "яйца", "fish" to "рыба", "soy" to "соя",
            ),
        ratePlace = "Оцените заведение",
        thanksRating = "Спасибо за оценку!",
        alreadyRated = "Вы уже оценили это заведение",
        submitFailed = "Не удалось — попробуйте ещё раз",
        tapStar = "Нажмите на звезду",
        waiter = "Официант",
        waiterComing = "Принято — уже идём!",
        tableWord = "Стол",
        callWaiter = "Позвать официанта",
        bringBill = "Принести счёт",
        myCheck = "Мой счёт",
        loading = "Загрузка…",
        noOrdersYet = "Заказов пока нет",
        checkHint = "Ваш счёт появится здесь, как только официант примет заказ.",
        scanTableTitle = "Отсканируйте QR-код вашего стола",
        scanTableHint = "Позвать официанта или попросить счёт можно после сканирования QR-кода на вашем столе.",
        total = "Итого",
        updatesLive = "Обновляется автоматически при изменении заказа",
        open = "Открыто",
        closed = "Закрыто",
        untilWord = "до",
        opensWord = "с",
        aboutUs = "О нас",
        addressLabel = "Адрес",
        phoneLabel = "Телефон",
        workingHours = "Часы работы",
        closedToday = "сегодня выходной",
        schedule = "Расписание",
        daysShort = listOf("Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"),
        closedDay = "выходной",
        followUs = "Мы в соцсетях",
        getDirections = "Проложить маршрут",
        contacts = "Контакты",
        visitWebsite = "Посетите наш сайт",
        information = "Информация",
        today = "Сегодня",
        allergenList = "Список аллергенов",
        allergenIntro = "В карточках блюд отмечаются такие аллергены: ",
        aboutCompany = "О компании",
        legal = "Правовая информация",
        cookiePolicy = "Политика использования файлов cookie",
        terms = "Условия использования",
        privacy = "Политика конфиденциальности",
        madeWith = "Создано на ",
        venueNotFound = "Заведение не найдено",
        menuUnavailable = "Меню временно недоступно",
        signInHint =
            "Авторизация упрощает процесс и запоминает ваши действия — " +
                "всё это можно увидеть в приложении QRkontaq.",
        continueWithGoogle = "Продолжить с Google",
        continueWithApple = "Продолжить с Apple",
        signOut = "Выйти из аккаунта",
        loyaltySectionTitle = "Программа лояльности",
        loyaltySectionDescription = "Зарегистрируйтесь, чтобы получить персональный QR-код и накапливать бонусы.",
        loyaltyCashbackTpl = "Кешбэк {p}% с чека",
        loyaltyBalanceLabel = "Ваш баланс",
        loyaltyRefreshCode = "Новый код",
        loyaltyCodeHint = "Покажите QR или назовите код официанту",
        loyaltyQrUnavailable = "QR недоступен — просто назовите код официанту",
        loyaltyLoadFailed = "Не удалось загрузить — попробуйте ещё раз",
        loyaltySignInFailed = "Не удалось войти — попробуйте ещё раз",
    )

private val MENU_DE =
    MenuStrings(
        popular = "Beliebt",
        chefRecommends = "Empfehlung des Küchenchefs",
        outOfStock = "Nicht verfügbar",
        kcalUnit = "kcal",
        allergensPrefix = "Allergene: ",
        close = "Schließen",
        share = "Teilen",
        linkCopied = "Link kopiert",
        filters = "Filter",
        reset = "Zurücksetzen",
        done = "Fertig",
        resetFilters = "Filter zurücksetzen",
        nothingFound = "Nichts gefunden",
        tryDifferentFilters = "Andere Filter ausprobieren",
        filterNames = mapOf("vegan" to "Vegan", "spicy" to "Scharf", "gluten-free" to "Glutenfrei"),
        tagNames = mapOf("vegan" to "vegan", "spicy" to "scharf", "new" to "neu"),
        allergenNames =
            mapOf(
                "gluten" to "Gluten", "lactose" to "Laktose", "nuts" to "Nüsse",
                "eggs" to "Eier", "fish" to "Fisch", "soy" to "Soja",
            ),
        ratePlace = "Lokal bewerten",
        thanksRating = "Danke für die Bewertung!",
        alreadyRated = "Sie haben dieses Lokal bereits bewertet",
        submitFailed = "Fehler — bitte erneut versuchen",
        tapStar = "Tippen Sie auf einen Stern",
        waiter = "Kellner",
        waiterComing = "Verstanden — wir kommen!",
        tableWord = "Tisch",
        callWaiter = "Kellner rufen",
        bringBill = "Rechnung bringen",
        myCheck = "Meine Rechnung",
        loading = "Wird geladen…",
        noOrdersYet = "Noch keine Bestellungen",
        checkHint = "Ihre Rechnung erscheint hier, sobald der Kellner Ihre Bestellung aufnimmt.",
        scanTableTitle = "Scannen Sie den QR-Code Ihres Tisches",
        scanTableHint = "Kellner rufen oder die Rechnung anfordern können Sie, nachdem Sie den QR-Code an Ihrem Tisch gescannt haben.",
        total = "Gesamt",
        updatesLive = "Aktualisiert sich automatisch bei Änderungen",
        open = "Geöffnet",
        closed = "Geschlossen",
        untilWord = "bis",
        opensWord = "öffnet",
        aboutUs = "Über uns",
        addressLabel = "Adresse",
        phoneLabel = "Telefon",
        workingHours = "Öffnungszeiten",
        closedToday = "heute geschlossen",
        schedule = "Wochenplan",
        daysShort = listOf("Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"),
        closedDay = "geschlossen",
        followUs = "Folgen Sie uns",
        getDirections = "Route planen",
        contacts = "Kontakt",
        visitWebsite = "Besuchen Sie unsere Website",
        information = "Informationen",
        today = "Heute",
        allergenList = "Allergenliste",
        allergenIntro = "Auf den Gerichtkarten sind diese Allergene markiert: ",
        aboutCompany = "Über das Unternehmen",
        legal = "Rechtliches",
        cookiePolicy = "Cookie-Richtlinie",
        terms = "Nutzungsbedingungen",
        privacy = "Datenschutzerklärung",
        madeWith = "Erstellt mit ",
        venueNotFound = "Lokal nicht gefunden",
        menuUnavailable = "Die Speisekarte ist vorübergehend nicht verfügbar",
        signInHint =
            "Die Anmeldung erleichtert den Prozess und merkt sich Ihre Aktionen — " +
                "alles in der QRkontaq-App.",
        continueWithGoogle = "Weiter mit Google",
        continueWithApple = "Weiter mit Apple",
        signOut = "Abmelden",
        loyaltySectionTitle = "Treueprogramm",
        loyaltySectionDescription = "Melden Sie sich an, um Ihren persönlichen QR-Code zu erhalten und Boni zu sammeln.",
        loyaltyCashbackTpl = "{p}% Cashback auf Ihre Rechnung",
        loyaltyBalanceLabel = "Ihr Guthaben",
        loyaltyRefreshCode = "Neuer Code",
        loyaltyCodeHint = "Zeigen Sie den QR-Code oder nennen Sie dem Kellner den Code",
        loyaltyQrUnavailable = "QR nicht verfügbar — nennen Sie dem Kellner einfach den Code",
        loyaltyLoadFailed = "Laden fehlgeschlagen — erneut versuchen",
        loyaltySignInFailed = "Anmeldung fehlgeschlagen — erneut versuchen",
    )

private val MENU_ES =
    MenuStrings(
        popular = "Popular",
        chefRecommends = "El chef recomienda",
        outOfStock = "Agotado",
        kcalUnit = "kcal",
        allergensPrefix = "Alérgenos: ",
        close = "Cerrar",
        share = "Compartir",
        linkCopied = "Enlace copiado",
        filters = "Filtros",
        reset = "Restablecer",
        done = "Listo",
        resetFilters = "Quitar filtros",
        nothingFound = "No hay resultados",
        tryDifferentFilters = "Prueba otros filtros",
        filterNames = mapOf("vegan" to "Vegano", "spicy" to "Picante", "gluten-free" to "Sin gluten"),
        tagNames = mapOf("vegan" to "vegano", "spicy" to "picante", "new" to "novedad"),
        allergenNames =
            mapOf(
                "gluten" to "gluten", "lactose" to "lactosa", "nuts" to "frutos secos",
                "eggs" to "huevos", "fish" to "pescado", "soy" to "soja",
            ),
        ratePlace = "Valora el local",
        thanksRating = "¡Gracias por tu valoración!",
        alreadyRated = "Ya has valorado este local",
        submitFailed = "No se pudo enviar — inténtalo de nuevo",
        tapStar = "Toca una estrella",
        waiter = "Camarero",
        waiterComing = "¡Recibido — ya vamos!",
        tableWord = "Mesa",
        callWaiter = "Llamar al camarero",
        bringBill = "Traer la cuenta",
        myCheck = "Mi cuenta",
        loading = "Cargando…",
        noOrdersYet = "Aún no hay pedidos",
        checkHint = "Tu cuenta aparecerá aquí en cuanto el camarero registre tu pedido.",
        scanTableTitle = "Escanea el código QR de tu mesa",
        scanTableHint = "Puedes llamar al camarero o pedir la cuenta tras escanear el código QR de tu mesa.",
        total = "Total",
        updatesLive = "Se actualiza automáticamente con tu pedido",
        open = "Abierto",
        closed = "Cerrado",
        untilWord = "hasta",
        opensWord = "abre",
        aboutUs = "Sobre nosotros",
        addressLabel = "Dirección",
        phoneLabel = "Teléfono",
        workingHours = "Horario de apertura",
        closedToday = "hoy cerrado",
        schedule = "Ver horario",
        daysShort = listOf("Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"),
        closedDay = "cerrado",
        followUs = "Síguenos",
        getDirections = "Cómo llegar",
        contacts = "Contacto",
        visitWebsite = "Visita nuestra web",
        information = "Información",
        today = "Hoy",
        allergenList = "Lista de alérgenos",
        allergenIntro = "Las fichas de los platos indican estos alérgenos: ",
        aboutCompany = "Sobre la empresa",
        legal = "Información legal",
        cookiePolicy = "Política de cookies",
        terms = "Términos del servicio",
        privacy = "Política de privacidad",
        madeWith = "Hecho con ",
        venueNotFound = "Local no encontrado",
        menuUnavailable = "El menú no está disponible temporalmente",
        signInHint =
            "Iniciar sesión facilita el proceso y recuerda tus acciones — " +
                "míralo todo en la app QRkontaq.",
        continueWithGoogle = "Continuar con Google",
        continueWithApple = "Continuar con Apple",
        signOut = "Cerrar sesión",
        loyaltySectionTitle = "Programa de fidelidad",
        loyaltySectionDescription = "Regístrate para obtener tu código QR personal y acumular bonos.",
        loyaltyCashbackTpl = "{p}% de cashback en tu cuenta",
        loyaltyBalanceLabel = "Tu saldo",
        loyaltyRefreshCode = "Nuevo código",
        loyaltyCodeHint = "Muestra el QR o dile el código al camarero",
        loyaltyQrUnavailable = "El QR no está disponible — dile el código al camarero",
        loyaltyLoadFailed = "No se pudo cargar — inténtalo de nuevo",
        loyaltySignInFailed = "No se pudo iniciar sesión — inténtalo de nuevo",
    )

private val MENU_FR =
    MenuStrings(
        popular = "Populaires",
        chefRecommends = "Le chef recommande",
        outOfStock = "Épuisé",
        kcalUnit = "kcal",
        allergensPrefix = "Allergènes : ",
        close = "Fermer",
        share = "Partager",
        linkCopied = "Lien copié",
        filters = "Filtres",
        reset = "Réinitialiser",
        done = "Terminé",
        resetFilters = "Réinitialiser les filtres",
        nothingFound = "Aucun résultat",
        tryDifferentFilters = "Essayez d'autres filtres",
        filterNames = mapOf("vegan" to "Végétalien", "spicy" to "Épicé", "gluten-free" to "Sans gluten"),
        tagNames = mapOf("vegan" to "végétalien", "spicy" to "épicé", "new" to "nouveau"),
        allergenNames =
            mapOf(
                "gluten" to "gluten", "lactose" to "lactose", "nuts" to "fruits à coque",
                "eggs" to "œufs", "fish" to "poisson", "soy" to "soja",
            ),
        ratePlace = "Évaluez l'établissement",
        thanksRating = "Merci pour votre note !",
        alreadyRated = "Vous avez déjà noté cet établissement",
        submitFailed = "Échec — réessayez",
        tapStar = "Touchez une étoile",
        waiter = "Serveur",
        waiterComing = "C'est noté — on arrive !",
        tableWord = "Table",
        callWaiter = "Appeler un serveur",
        bringBill = "Apporter l'addition",
        myCheck = "Mon addition",
        loading = "Chargement…",
        noOrdersYet = "Pas encore de commandes",
        checkHint = "Votre addition apparaîtra ici dès que le serveur enregistrera votre commande.",
        scanTableTitle = "Scannez le QR code de votre table",
        scanTableHint = "Appelez un serveur ou demandez l'addition après avoir scanné le QR code de votre table.",
        total = "Total",
        updatesLive = "Mise à jour automatique à chaque changement",
        open = "Ouvert",
        closed = "Fermé",
        untilWord = "jusqu'à",
        opensWord = "ouvre à",
        aboutUs = "À propos",
        addressLabel = "Adresse",
        phoneLabel = "Téléphone",
        workingHours = "Heures d'ouverture",
        closedToday = "fermé aujourd'hui",
        schedule = "Horaires",
        daysShort = listOf("Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"),
        closedDay = "fermé",
        followUs = "Suivez-nous",
        getDirections = "Itinéraire",
        contacts = "Contact",
        visitWebsite = "Visitez notre site",
        information = "Informations",
        today = "Aujourd'hui",
        allergenList = "Liste des allergènes",
        allergenIntro = "Les fiches des plats indiquent ces allergènes : ",
        aboutCompany = "À propos de l'entreprise",
        legal = "Mentions légales",
        cookiePolicy = "Politique de cookies",
        terms = "Conditions d'utilisation",
        privacy = "Politique de confidentialité",
        madeWith = "Créé avec ",
        venueNotFound = "Établissement introuvable",
        menuUnavailable = "Le menu est temporairement indisponible",
        signInHint =
            "La connexion simplifie le processus et mémorise vos actions — " +
                "retrouvez tout dans l'app QRkontaq.",
        continueWithGoogle = "Continuer avec Google",
        continueWithApple = "Continuer avec Apple",
        signOut = "Se déconnecter",
        loyaltySectionTitle = "Programme de fidélité",
        loyaltySectionDescription = "Inscrivez-vous pour obtenir votre QR code personnel et cumuler des bonus.",
        loyaltyCashbackTpl = "{p}% de cashback sur l'addition",
        loyaltyBalanceLabel = "Votre solde",
        loyaltyRefreshCode = "Nouveau code",
        loyaltyCodeHint = "Montrez le QR ou donnez le code au serveur",
        loyaltyQrUnavailable = "QR indisponible — donnez simplement le code au serveur",
        loyaltyLoadFailed = "Échec du chargement — réessayez",
        loyaltySignInFailed = "Échec de la connexion — réessayez",
    )

/** Strings of the guest menu UI for [lang]; unknown codes fall back to English. */
internal fun menuT(lang: String): MenuStrings =
    when (lang) {
        "en" -> MENU_EN
        "uk" -> MENU_UK
        "pl" -> MENU_PL
        "ru" -> MENU_RU
        "de" -> MENU_DE
        "es" -> MENU_ES
        "fr" -> MENU_FR
        else -> MENU_EN
    }
